'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import styles from './medical.module.css';

const steps = [
  {
    num: '01',
    title: 'Share Your Practice Details',
    desc: 'Upload your services, hours, and location — or drop in your website URL and we extract everything automatically.',
    illus: (
      <div className={styles.illusStack}>
        <div className={`${styles.illusDoc} ${styles.illusDocBack2}`}>
          <span>yourpractice.com</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocBack1}`}>
          <span>Office Hours</span>
        </div>
        <div className={`${styles.illusDoc} ${styles.illusDocFront}`}>
          <span className={styles.illusDocIcon}>📋</span>
          <span>Services.pdf</span>
        </div>
        <div className={styles.illusAgent}>
          <span>🤖</span>
          <span>Medical Agent</span>
        </div>
      </div>
    ),
  },
  {
    num: '02',
    title: 'Train Your Agent',
    desc: 'Name your agent, customize its greeting and tone, connect your EHR system (Epic, NexHealth), and set language preferences.',
    illus: (
      <div className={styles.illusPanel}>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Agent Name</span>
          <span className={styles.illusPanelValue}>Nova</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Tone</span>
          <span className={styles.illusPanelValue}>Calm &amp; Professional</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>EHR</span>
          <span className={styles.illusPanelValue}>Epic · NexHealth</span>
        </div>
        <div className={styles.illusPanelRow}>
          <span className={styles.illusPanelLabel}>Appointments</span>
          <span className={`${styles.illusPanelValue} ${styles.illusPanelOn}`}>Enabled ✓</span>
        </div>
      </div>
    ),
  },
  {
    num: '03',
    title: 'Claim Your Phone Number',
    desc: 'Choose a dedicated local number for your practice. Forward your existing number or start using the new one immediately.',
    illus: (
      <div className={styles.illusPhone}>
        <div className={styles.illusPhoneLabel}>Your Local Number</div>
        <div className={styles.illusPhoneNum}>(555) 312-4400</div>
        <div className={styles.illusPhoneBadge}>✓ Ready to receive calls</div>
      </div>
    ),
  },
  {
    num: '04',
    title: 'Go Live 24/7',
    desc: 'Your medical agent is on. Every patient call answered, every appointment captured — no hold times, no missed calls.',
    illus: (
      <div className={styles.illusLive}>
        <div className={styles.illusLiveBadge}>
          <span className={styles.illusLiveDot} />
          LIVE
        </div>
        <ul className={styles.illusLiveList}>
          <li>No hold times</li>
          <li>No missed appointments</li>
          <li>No extra staff needed</li>
        </ul>
      </div>
    ),
  },
];

export default function MedicalAgentPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const goTo = (idx: number) => {
    const clamped = Math.max(0, Math.min(idx, steps.length - 1));
    setCurrentSlide(clamped);
    const vp = viewportRef.current;
    if (!vp) return;
    const cards = vp.querySelectorAll('[data-card]');
    cards[clamped]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };

  return (
    <div className={styles.page}>

      {/* ── Navigation ── */}
      <NavBar />

      {/* ── Hero ── */}
      <section className={styles.hero}>
        <Image
          src="/images/medical.png"
          alt="Medical background"
          fill
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroLayout}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>Medical AI Voice Agent</span>
            <h1 className={styles.heroTitle}>Your Patients Deserve Better Than Hold Music.</h1>
            <p className={styles.heroSubtitle}>
              Your AI agent verifies patient identity, schedules appointments, checks prescription status, and handles insurance inquiries — around the clock, no exceptions.
            </p>
            <div className={styles.heroBtns}>
              <Link href="/register?industry=medical" className={styles.heroBtnPrimary}>Get Started</Link>
              <Link href="#how-it-works" className={styles.heroBtnSecondary}>See How It Works</Link>
            </div>
          </div>

          {/* ── Book Demo Widget ── */}
          <div className={styles.audioWidget}>
            <div className={styles.audioWidgetHeader}>
              <div className={styles.audioAvatar}>AI</div>
              <div>
                <div className={styles.audioAgentName} style={{ marginLeft: '44px' }}>Cara</div>
                <div className={styles.audioAgentStatus}>
                  <span className={styles.audioStatusDotActive} />
                  Medical AI Agent
                </div>
              </div>
            </div>
            <div className={styles.audioWaveform}>
              {[...Array(16)].map((_, i) => (
                <span key={i} className={`${styles.audioWaveBar} ${styles.audioWaveBarActive}`} style={{ animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <Link href="/contact" className={styles.audioPlayBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              Book a Live Demo
            </Link>
            <p className={styles.audioLabel}>Free 15-minute demo · No commitment</p>
          </div>

        </div>
      </section>

      {/* ── Features ── */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresInner}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>HIPAA COMPLIANT · ALWAYS ON · INSTANTLY INTELLIGENT</span>
            <h2>Every Patient Call Answered.<br />Every Appointment Captured.<br />Every Time.</h2>
            <p>NovaMind AI handles your phone lines so your staff doesn&apos;t have to — scheduling appointments, verifying patients, and answering inquiries 24/7 with zero hold times.</p>
          </div>
          <div className={styles.featuresGrid}>

            {/* Card 1 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusCallWrap}>
                  <div className={styles.illusCallBadge}>
                    <span className={styles.illusCallDot} /> Live · Incoming Call
                  </div>
                  <div className={styles.illusCallNumber}>(555) 312-4400</div>
                  <div className={styles.illusWaveBars}>
                    {[...Array(12)].map((_, i) => (
                      <span key={i} className={styles.illusWaveBar} style={{ animationDelay: `${i * 0.08}s` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Instant Call Answering & Routing</h3>
                <p className={styles.fcSubtitle}>AI answers 100% of inbound calls and routes to the right department with zero hold time.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusApptCard}>
                  <div className={styles.illusApptTitle}>New Appointment</div>
                  <div className={styles.illusApptRow}><span>Patient</span><span>James Carter</span></div>
                  <div className={styles.illusApptRow}><span>Date</span><span>Mon, Apr 7</span></div>
                  <div className={styles.illusApptRow}><span>Time</span><span>10:30 AM</span></div>
                  <div className={styles.illusApptRow}><span>Provider</span><span>Dr. Nguyen</span></div>
                  <div className={styles.illusApptConfirm}>✓ Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Appointment Scheduling</h3>
                <p className={styles.fcSubtitle}>Books, confirms, reschedules, and cancels appointments automatically — 24/7.</p>
              </div>
            </div>

            {/* Card 3 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusVerifyCard}>
                  <div className={styles.illusVerifyTitle}>Patient Verification</div>
                  <div className={styles.illusVerifyRow}><span>Name</span><span>Emily Rosales</span></div>
                  <div className={styles.illusVerifyRow}><span>DOB</span><span>03 / 14 / 1988</span></div>
                  <div className={styles.illusVerifyRow}><span>Member ID</span><span>BCB-009214</span></div>
                  <div className={styles.illusVerifyBadge}>✓ Identity Verified</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Patient Identity Verification</h3>
                <p className={styles.fcSubtitle}>Securely verifies patient identity before connecting to staff or providing information.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusRxCard}>
                  <div className={styles.illusRxTitle}>Prescription Status</div>
                  <div className={styles.illusRxRow}><span>Medication</span><span>Lisinopril 10mg</span></div>
                  <div className={styles.illusRxRow}><span>Refills Left</span><span>2</span></div>
                  <div className={styles.illusRxRow}><span>Status</span><span className={styles.illusRxReady}>Ready for pickup</span></div>
                  <div className={styles.illusRxBadge}>✓ Prescription Found</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Prescription Status Inquiries</h3>
                <p className={styles.fcSubtitle}>Tells patients their prescription status, refill count, and pickup availability.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusInsCard}>
                  <div className={styles.illusInsTitle}>Insurance Inquiry</div>
                  <div className={styles.illusInsRow}><span>Provider</span><span>Blue Cross</span></div>
                  <div className={styles.illusInsRow}><span>Plan</span><span>PPO Gold</span></div>
                  <div className={styles.illusInsRow}><span>Deductible Met</span><span>$800 / $1,500</span></div>
                  <div className={styles.illusInsRow}><span>Copay</span><span>$30 per visit</span></div>
                  <div className={styles.illusInsBadge}>✓ Coverage Confirmed</div>
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Insurance Coverage Inquiries</h3>
                <p className={styles.fcSubtitle}>Answers patient questions about coverage, copays, deductibles, and in-network status.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className={styles.featureCard}>
              <div className={styles.fcIllusArea}>
                <div className={styles.illusLangWrap}>
                  {[
                    { lang: 'EN', text: 'I need to book an appointment.' },
                    { lang: 'ES', text: 'Necesito reservar una cita.' },
                    { lang: 'ZH', text: '我需要预约一个诊位。' },
                  ].map((b, i) => (
                    <div key={i} className={styles.illusLangBubble}>
                      <span className={styles.illusLangTag}>{b.lang}</span>
                      <span className={styles.illusLangText}>{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.fcTextArea}>
                <h3 className={styles.fcTitle}>Multi-language on Request</h3>
                <p className={styles.fcSubtitle}>Configured for the languages your patients speak — add support based on your community&apos;s needs.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className={styles.howItWorksSection}>
        <div className={styles.howItWorksHeader}>
          <div>
            <h2 className={styles.howTitle}>
              <em className={styles.howTitleItalic}>How</em> It Works.
            </h2>
            <p className={styles.howSubtitle}>From sign-up to live patient calls in under 5 minutes</p>
          </div>
          <div className={styles.sliderArrows}>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide - 1)}
              disabled={currentSlide === 0}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className={styles.arrowBtn}
              onClick={() => goTo(currentSlide + 1)}
              disabled={currentSlide === steps.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>

        <div className={styles.sliderViewport} ref={viewportRef}>
          {steps.map((step, i) => (
            <div
              key={i}
              data-card
              className={`${styles.slideCard} ${styles[`slideCard${i + 1}`]}`}
            >
              <span className={styles.slideNum}>{step.num}</span>
              <div className={styles.slideIllus}>{step.illus}</div>
              <div className={styles.slideText}>
                <h3 className={styles.slideTitle}>{step.title}</h3>
                <p className={styles.slideDesc}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <Image
          src="/images/poster.png"
          alt="CTA background"
          fill
          priority
          className={styles.ctaImage}
        />
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Medical Practice?</h2>
          <p className={styles.ctaSubtitle}>Every patient treated with the same warmth, care, and professionalism — 24/7, from the very first ring.</p>
          <Link href="/register?industry=medical" className={styles.ctaBtn}>Get Started</Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <Footer />

    </div>
  );
}
